<svelte:options tag={null} />
<script lang="typescript">
  import { get_current_component } from "svelte/internal";
  import { isPermissionPresent } from '@everymatrix/retail-helpers';
  import "@everymatrix/em-card/dist/em-card";

  export let src: string = null;
  export let playersrc: string = null;

  let currency: string;
  let accounts: number;
  let paycards: number;

  let errorMessage: string;
  let isLoading = false;
  let permissions;
  permissions = {};

  const thisComponent = get_current_component();

  const dispatchWcEvent = (name, detail) => {
    thisComponent.dispatchEvent(
      new CustomEvent(name, {
        detail,
        composed: true,
      })
    );
  };

  const getHeaders = () => {
    const headerHandler = (e: MessageEvent, resolve: Function, ref: any) => {
      if (e.data?.type === "SetAuthHeaders") {
        // Resolve the promise
        window.removeEventListener("message", ref);
        resolve(e.data.payload);
        // Remove event listner
      }
    };
    // Send the post message to get auth headers
    window.postMessage({ type: "GetAuthHeaders" }, "*");

    return new Promise<HeadersInit>((resolve, reject) => {
      // Create a referance for that function in order to remove the event listener
      let ref = (e) => headerHandler(e, resolve, ref);
      console.log("%c...waiting for auth headers", "color: #135db1");
      // Add event listener to get SetAuthHeaders
      window.addEventListener("message", ref);
    });
  };

  const getCurrency = async (playersrc: string, headers: HeadersInit) => {
    try {
      const res = await fetch(playersrc, { headers });
      const data = await res.json();
      if (res.ok) {
        currency = data.currency;
      } else {
        throw new Error("Failed to fetch currency");
      }
    } catch (error) {
      throw new Error(error);
    }
  };

  const getAccounts = async (playersrc: string, headers: HeadersInit) => {
    try {
      const res = await fetch(playersrc, { headers });
      const { data } = await res.json();
      if (res.ok) {
        accounts = data.length;
      } else {
        throw new Error("Failed to fetch accounts");
      }
    } catch (error) {
      throw new Error(error);
    }
  };

  const getPayCards = async (playersrc: string, headers: HeadersInit) => {
    try {
      const res = await fetch(playersrc, { headers });
      const { total } = await res.json();
      if (res.ok) {
        paycards = total;
      } else {
        throw new Error("Failed to fetch pay cards");
      }
    } catch (error) {
      throw new Error(error);
    }
  };

  const getData = async (playersrc: string) => {
    try {
      isLoading = true;
      const headers = await getHeaders();
      await Promise.all([
        getCurrency(`${playersrc}/profile`, headers),
        getAccounts(`${playersrc}/playerAccount`, headers),
        getPayCards(`${playersrc}/paycards`, headers),
      ]);
    } catch (error) {
      errorMessage = error;
    } finally {
      isLoading = false;
    }
  };

  const getPemissions = async () => {
    [ permissions.canReadAccounts, 
      permissions.canReadPayCards] = await Promise.all([
        isPermissionPresent(src, 'upp.nav.accounts', 'READ'), 
        isPermissionPresent(src, 'upp.nav.paycards', 'READ')
      ]);
  }
  $: src && getPemissions();
  $: playersrc && getData(playersrc);
</script>

<em-card>
  <h3 slot="header">
    Transaction Info
  </h3>

  <div slot="content">
    {#if isLoading}
      <ul class="loader">
        <li />
        <li />
        <li />
      </ul>
    {:else}
      <ul>
        <li>
          <strong>Player Currency:</strong>
          <span>
            {currency ?? ' - '}
          </span>
        </li>
        <li>
          <strong>Accounts:</strong>
          {#if permissions.canReadAccounts}
            <span>
              <a
                href="/"
                class="anchor"
                on:click|preventDefault={() =>
                  dispatchWcEvent("navigate", "player-accounts-page")}
              >
                {accounts ?? " 0 "}
              </a>
            </span>
          {:else}
            <span>{accounts ?? " 0 "}</span>
          {/if}
        </li>
        <li>
          <strong>PayCards:</strong>
          {#if permissions.canReadPayCards}
            <span>
              <a
                href="/"
                class="anchor"
                on:click|preventDefault={() =>
                  dispatchWcEvent("navigate", "player-pay-cards-page")}
              >
                {paycards ?? " 0 "}
              </a>
            </span>
          {:else}
            <span>{paycards ?? " 0 "}</span>
          {/if}
        </li>
      </ul>
      {#if !(currency && paycards && currency) && errorMessage}
        <p>{errorMessage}</p>
      {/if}
    {/if}
  </div>
</em-card>

<style lang="scss">
  *,
  *::before,
  *::after {
    margin: 0;
    padding: 0;
    list-style: none;
    outline: none;
    text-decoration: none;
    box-sizing: border-box;
    font-family: "Helvetica Neue", "Helvetica", sans-serif;
  }
  .anchor {
    display: block;
    color: #348dff;
    font-size: 16px;
    text-decoration: underline;
    @media (min-width: 580px) {
      font-size: 14px;
    }
  }

  .link {
    color: #307fe2;
    font-weight: 800;
    cursor: pointer;
  }

  li {
    display: grid;
    grid-template-columns: auto 1fr;
    margin-bottom: 10px;
    &:last-child {
      margin-bottom: 0;
    }

    strong { margin-right: 10px; }

    span { align-self: flex-end; }
  }

  // Text Loader
  .loader {
    min-width: 200px;

    li {
      position: relative;
      height: 18px;
      margin-bottom: 10px;
      animation-duration: 1.25s;
      animation-fill-mode: forwards;
      animation-iteration-count: infinite;
      animation-name: shimmer;
      animation-timing-function: linear;
      background: #f6f6f6;
      background: linear-gradient(
        to right,
        #f6f6f6 8%,
        #f0f0f0 18%,
        #f5f5f5 33%
      );
      background-size: 800px 104px;
      &:last-child {
        margin-bottom: 0;
      }
    }
  }
  // Animation
  @keyframes shimmer {
    0% {
      background-position: -468px 0;
    }
    100% {
      background-position: 468px 0;
    }
  }
</style>
